import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import siteConfig from '../config/site-config';
import { applyMapboxToken } from '../utils/mapboxToken';
import { attachCoordAdapter, DATUM_WGS84 } from '../utils/dashboard/basemapCoord';
import {
  buildCaptureStatusText,
  formatLngLat,
  haversineMeters,
  formatStraightDistance,
  hasMeaningfulManualPoint,
  PICK_SAMPLE_COLORS,
  PICK_AUTO_COLOR,
  PICK_MANUAL_COLOR
} from '../utils/pickPointCapture';
import usePickPointCapture from '../hooks/usePickPointCapture';

applyMapboxToken(mapboxgl);

function PickPointPage() {
  const token = new URLSearchParams(window.location.search).get('t') || '';
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const sampleMarkersRef = useRef([]);
  const liveMarkerRef = useRef(null);
  const autoMarkerRef = useRef(null);
  const manualMarkerRef = useRef(null);
  const editingRef = useRef(false);
  const submitManualRef = useRef(() => {});
  const flewToRef = useRef(false);
  const [linkInfo, setLinkInfo] = useState(null);
  const [captureSettings, setCaptureSettings] = useState(null);
  const [alreadyUsed, setAlreadyUsed] = useState(false);
  const [bootError, setBootError] = useState('');
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);

  const capture = usePickPointCapture({
    token,
    enabled: !!linkInfo && mapReady && !loading,
    initialPoint: linkInfo,
    alreadyUsed,
    captureSettings
  });

  editingRef.current = capture.editing;
  submitManualRef.current = capture.submitManual;

  useEffect(() => {
    if (!token) {
      setBootError('缺少选点令牌');
      setLoading(false);
      return;
    }
    fetch(`${siteConfig.getApiUrl()}/pick-point/check?t=${encodeURIComponent(token)}`)
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.error || '选点链接不可用');
        setLinkInfo(data.token);
        setCaptureSettings(data.capture_settings || null);
        setAlreadyUsed(!!data.already_used);
      })
      .catch((requestError) => setBootError(requestError.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return undefined;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [119.15, 28.07],
      zoom: 10,
      attributionControl: false
    });
    // Mapbox 样式即 WGS84：显式声明，选点直接按 WGS84 提交
    attachCoordAdapter(map, { datum: DATUM_WGS84, basemapId: 'pick-point-mapbox' });
    map.on('load', () => setMapReady(true));
    map.on('click', (event) => {
      if (!editingRef.current) return;
      submitManualRef.current(event.lngLat.lng, event.lngLat.lat);
    });
    mapRef.current = map;
    return () => {
      sampleMarkersRef.current.forEach((marker) => marker.remove());
      liveMarkerRef.current?.remove();
      autoMarkerRef.current?.remove();
      manualMarkerRef.current?.remove();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    sampleMarkersRef.current.forEach((marker) => marker.remove());
    sampleMarkersRef.current = capture.samples.map((sample) => new mapboxgl.Marker({
      color: sample.color || PICK_SAMPLE_COLORS[sample.seq - 1],
      scale: 0.85
    }).setLngLat([sample.lng, sample.lat]).addTo(map));
  }, [capture.samples]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (capture.latestFix && !capture.finalized && !capture.awaitingConfirm) {
      const lngLat = [capture.latestFix.lng, capture.latestFix.lat];
      if (!liveMarkerRef.current) {
        liveMarkerRef.current = new mapboxgl.Marker({ color: '#64748b', scale: 0.7 }).setLngLat(lngLat).addTo(map);
      } else {
        liveMarkerRef.current.setLngLat(lngLat);
      }
      if (!flewToRef.current) {
        flewToRef.current = true;
        map.flyTo({ center: lngLat, zoom: 16, essential: true });
      }
    } else if (liveMarkerRef.current) {
      liveMarkerRef.current.remove();
      liveMarkerRef.current = null;
    }
  }, [capture.awaitingConfirm, capture.finalized, capture.latestFix]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !capture.autoPoint) return;
    const lngLat = [capture.autoPoint.lng, capture.autoPoint.lat];
    if (!autoMarkerRef.current) {
      autoMarkerRef.current = new mapboxgl.Marker({ color: PICK_AUTO_COLOR }).setLngLat(lngLat).addTo(map);
    } else {
      autoMarkerRef.current.setLngLat(lngLat);
    }
    if (!flewToRef.current) {
      flewToRef.current = true;
      map.flyTo({ center: lngLat, zoom: 16, essential: true });
    }
  }, [capture.autoPoint]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const meaningful = hasMeaningfulManualPoint(capture.autoPoint, capture.manualPoint);
    if (!meaningful && !capture.editing) {
      manualMarkerRef.current?.remove();
      manualMarkerRef.current = null;
      return;
    }
    const pos = meaningful
      ? [capture.manualPoint.lng, capture.manualPoint.lat]
      : (capture.autoPoint ? [capture.autoPoint.lng, capture.autoPoint.lat] : null);
    if (!pos) return;
    if (!manualMarkerRef.current) {
      manualMarkerRef.current = new mapboxgl.Marker({ color: PICK_MANUAL_COLOR, draggable: true })
        .setLngLat(pos)
        .addTo(map);
      manualMarkerRef.current.on('dragend', () => {
        const position = manualMarkerRef.current.getLngLat();
        submitManualRef.current(position.lng, position.lat);
      });
    } else if (!capture.editing) {
      manualMarkerRef.current.setLngLat(pos);
    }
  }, [capture.autoPoint, capture.editing, capture.manualPoint]);

  const statusText = buildCaptureStatusText({
    phase: capture.phase,
    seq: capture.recordingSeq,
    accuracy: capture.latestFix?.accuracy,
    autoPoint: capture.autoPoint,
    manualPoint: capture.manualPoint,
    recordAccuracyMaxM: capture.thresholds?.recordAccuracyMaxM
  });
  const showManual = hasMeaningfulManualPoint(capture.autoPoint, capture.manualPoint);
  const distanceText = showManual
    ? formatStraightDistance(haversineMeters(capture.autoPoint, capture.manualPoint))
    : '';
  const displayError = bootError || capture.error;
  const showCapture = !!linkInfo && !loading;
  const canEdit = capture.finalized && !capture.confirming && !capture.confirmStatus?.confirmed;
  const showLiveCoords = capture.latestFix
    && capture.phase !== 'finalized'
    && capture.phase !== 'amended'
    && capture.phase !== 'editing'
    && capture.phase !== 'preview';
  
  // 显示手动选点按钮：当定位失败或超时
  const showManualPickButton = displayError && !capture.finalized && !capture.awaitingConfirm;
  
  // 确认状态提示
  const confirmStatusText = capture.finalized && !capture.confirmStatus?.confirmed
    ? '已提交位置信息，等待救援人员确认。'
    : capture.confirmStatus?.confirmed
    ? capture.confirmStatus.message
    : null;

  return (
    <main style={{ position: 'fixed', inset: 0, overflow: 'hidden', background: '#eaf2f8', fontFamily: 'system-ui, sans-serif' }}>
      <div ref={mapContainerRef} style={{ position: 'absolute', inset: 0 }} />
      <section style={{ position: 'absolute', zIndex: 2, left: 12, right: 12, bottom: 16, maxWidth: 560, margin: '0 auto', padding: 16, borderRadius: 12, background: 'rgba(255,255,255,.96)', boxShadow: '0 6px 24px rgba(0,0,0,.22)' }}>
        <h1 style={{ margin: '0 0 6px', fontSize: 21, color: '#c2410c' }}>临时位置上报工具</h1>
        {loading && <p style={{ margin: 0 }}>正在验证选点链接…</p>}
        {displayError && <p style={{ margin: '8px 0', color: '#b42318' }}>{displayError}</p>}
        {!loading && !displayError && !linkInfo && <p style={{ margin: 0 }}>选点链接不可用。</p>}
        {showCapture && (
          <>
            <p style={{ margin: '0 0 10px', color: '#475569' }}>
              {linkInfo.note || '失踪人员专用'}
            </p>
            {capture.samples.length < 3 && !capture.finalized && (
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
                正在定位，请保持手机处于室外，视野开阔环境，以保障定位精度。
              </p>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
              <p style={{ margin: 0, flex: 1, fontSize: 14, lineHeight: 1.5, color: capture.phase === 'too_large' ? '#b42318' : '#0f172a' }}>
                {statusText}
                {showLiveCoords ? (
                  <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: '#64748b' }}>
                    当前：{formatLngLat(capture.latestFix)}（经度, 纬度）
                  </span>
                ) : null}
                {distanceText ? (
                  <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: '#0369a1' }}>{distanceText}</span>
                ) : null}
              </p>
            </div>
            {confirmStatusText && (
              <div style={{ 
                padding: '12px 16px', 
                marginBottom: 10, 
                borderRadius: 8, 
                background: capture.confirmStatus?.confirmed ? '#d1fae5' : '#fef3c7',
                border: `1px solid ${capture.confirmStatus?.confirmed ? '#10b981' : '#f59e0b'}`,
                fontSize: 14,
                lineHeight: 1.5,
                color: capture.confirmStatus?.confirmed ? '#065f46' : '#78350f'
              }}>
                {capture.confirmStatus?.confirmed ? '✅ ' : '⏳ '}{confirmStatusText}
              </div>
            )}
            {capture.samples.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, fontSize: 12, color: '#475569' }}>
                {capture.samples.map((sample) => (
                  <span key={sample.seq} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: sample.color }} />
                    第{sample.seq}次{Number.isFinite(sample.accuracy) ? ` ${Math.round(sample.accuracy)}米` : ''}
                  </span>
                ))}
              </div>
            )}
            {capture.confirming && (
              <div style={{ 
                padding: '12px 16px', 
                marginBottom: 10, 
                textAlign: 'center',
                color: '#475569',
                fontSize: 14
              }}>
                正在提交位置信息…
              </div>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={capture.startEdit}
                style={{
                  display: 'block',
                  width: '100%',
                  marginBottom: 8,
                  padding: 0,
                  border: 0,
                  background: 'transparent',
                  color: '#94a3b8',
                  fontSize: 12,
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                位置不准？点此修改（可选）
              </button>
            )}
            {!capture.finalized && (
              <button
                type="button"
                onClick={capture.retryLocate}
                style={{ width: '100%', minHeight: 40, border: 0, borderRadius: 7, background: '#2563eb', color: '#fff', fontWeight: 700 }}
              >
                重新定位
              </button>
            )}
            {showManualPickButton && (
              <button
                type="button"
                onClick={() => {
                  capture.setError('');
                  // 使用一个临时的自动坐标来启用地图点击
                  const defaultLat = 28.07;
                  const defaultLng = 119.15;
                  capture.submitManual(defaultLng, defaultLat);
                  alert('定位失败，请在地图上点击您的当前位置');
                }}
                style={{ 
                  width: '100%', 
                  minHeight: 40, 
                  marginTop: 8,
                  border: 0, 
                  borderRadius: 7, 
                  background: '#f59e0b', 
                  color: '#fff', 
                  fontWeight: 700 
                }}
              >
                手动在地图选点
              </button>
            )}
          </>
        )}
      </section>
    </main>
  );
}

export default PickPointPage;
